import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { MessagesInformationService } from '../messages-information.service';
import { Message } from '../model/message.model';
import { MessageCreateRequest } from '../request/create-request';
import { MessageUpdateRequest } from '../request/update-request';

export function avecMéthodesMessages<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Message>>(),
      props: type<EntityProps<Message>>(),
    },
    withMethods((store) => ({
      chargerMessages: (state: { messages: Message[] }) => patchState(store, setAllEntities(state.messages)),
      ajouterMessage: (state: { message: Message }) => patchState(store, upsertEntity(state.message)),
      modifierMessage: (state: { message: Message }) =>
        patchState(store, updateEntity({ id: state.message.id, changes: state.message })),
      supprimerMessage: (id: string) => patchState(store, removeEntity(id)),
    })),
    withMethods((store, service = inject(MessagesInformationService)) => ({
      modifier: rxMethod<{ id: string; data: MessageUpdateRequest; next: () => void }>(
        pipe(
          switchMap(({ id, data, next }) =>
            service.modifier(id, data).pipe(
              tapResponse({
                next: (message: Message) => {
                  store.modifierMessage({ message });
                  next();
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modifications de ce message, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      ajouter: rxMethod<{
        data: MessageCreateRequest;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ data, next }) =>
            service.créer(data).pipe(
              tapResponse({
                next: (message) => {
                  next?.();
                  store.ajouterMessage({ message });
                },
                error: (err) => {
                  console.error("Error lors de l'ajout d'un message via le Store: ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création du message, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      charger: rxMethod<boolean>(
        pipe(
          switchMap((actifs) =>
            service.trouverTous(actifs).pipe(
              tapResponse({
                next: (response) => store.chargerMessages({ messages: response.items }),
                error: (err) => {
                  console.error('Error lors du chargement des messages via le Store: ', err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors du chargement des messages, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<string>(
        pipe(
          switchMap((id) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => store.supprimerMessage(id),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression du message, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
